import { Link, useNavigate } from 'react-router';
import { Share2, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { EmptyState, FeedDot, VisibilityBadge } from '@/components/app/common';
import { useFeed, useLayers } from '@/lib/queries';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { FeedDetail } from '@/lib/types';
import { lazy, Suspense } from 'react';
import { SettingsTab } from './SettingsTab';
import { HistoryTab } from './HistoryTab';

// The calendar pulls in FullCalendar; load the heavier tabs only when they're opened.
const CalendarTab = lazy(() => import('./CalendarTab').then((m) => ({ default: m.CalendarTab })));
const LayersTab = lazy(() => import('./LayersTab').then((m) => ({ default: m.LayersTab })));
const ShareTab = lazy(() => import('./ShareTab').then((m) => ({ default: m.ShareTab })));
const AudienceTab = lazy(() => import('./AudienceTab').then((m) => ({ default: m.AudienceTab })));

export interface TabProps {
  id: string;
  detail: FeedDetail;
  canEdit: boolean;
}

const TABS: [string, string][] = [
  ['calendar', 'Calendar'],
  ['layers', 'Layers'],
  ['share', 'Share'],
  ['audience', 'Audience'],
  ['settings', 'Settings'],
  ['history', 'History'],
];

export function FeedPage({ id, tab }: { id: string; tab: string }) {
  const q = useFeed(id);
  const navigate = useNavigate();
  const detail = q.data;
  // Open same-level conflicts are flagged on the Layers tab from every page.
  const layers = useLayers(id, !!detail && (detail.includes.length > 1 || tab === 'layers'));

  if (q.isPending)
    return (
      <div className="grid grid-cols-[minmax(0,1fr)] gap-4">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-9 w-full max-w-xl" />
        <Skeleton className="h-96 w-full rounded-xl" />
      </div>
    );
  if (q.isError || !detail)
    return (
      <EmptyState title="Calendar not available" body={q.error instanceof ApiError ? q.error.message : 'Something went wrong.'}>
        <Button asChild>
          <Link to="/">Back to your calendars</Link>
        </Button>
      </EmptyState>
    );

  const { feed, role } = detail;
  const canEdit = role !== 'viewer';
  document.title = `${feed.name} · Calendar Feed`;
  const needs = layers.data?.needs_decision ?? 0;
  const props: TabProps = { id, detail, canEdit };

  return (
    <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-5">
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-3">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Calendars</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{feed.name}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex flex-wrap items-center gap-3">
          <FeedDot color={feed.color} className="size-3.5 rounded-[4px]" />
          <h1 className="min-w-0 truncate text-2xl font-semibold tracking-tight">{feed.name}</h1>
          <div className="flex flex-wrap gap-1.5">
            {feed.kind === 'external' ? <Badge variant="outline">External</Badge> : null}
            <VisibilityBadge v={feed.visibility} />
            {role !== 'owner' ? <Badge variant="outline">You’re {role === 'editor' ? 'an editor' : 'a viewer'}</Badge> : null}
          </div>
          {tab !== 'share' ? (
            <Button className="ml-auto" onClick={() => navigate(`/feeds/${id}/share`)}>
              <Share2 /> Share
            </Button>
          ) : null}
        </div>
        {feed.pinned_version ? (
          <div className="border-warning/40 bg-warning/10 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
            <TriangleAlert className="text-warning-foreground size-4 shrink-0" />
            Subscribers are seeing version {feed.pinned_version} (rolled back). New changes won’t reach them until you resume live updates in History.
          </div>
        ) : null}
        <nav aria-label="Calendar sections" className="tabs -mb-px flex gap-1 overflow-x-auto border-b">
          {TABS.map(([k, label]) => (
            <Link
              key={k}
              to={`/feeds/${id}/${k}`}
              aria-current={k === tab ? 'page' : undefined}
              className={cn('hover:text-foreground flex shrink-0 items-center gap-1.5 border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground transition-colors', k === tab && 'border-foreground text-foreground')}
            >
              {label}
              {k === 'layers' && needs ? (
                <span className="count warn bg-warning text-background grid h-[18px] min-w-[18px] place-items-center rounded-full px-1 text-[11px] font-semibold" title={`${needs} conflicts need a decision`}>
                  {needs}
                </span>
              ) : null}
            </Link>
          ))}
        </nav>
      </div>
      <div className={cn('min-w-0', !['calendar', 'layers'].includes(tab) && 'max-w-5xl')}>
        <Suspense fallback={<Skeleton className="h-[480px] w-full rounded-xl" />}>
          {tab === 'calendar' ? <CalendarTab {...props} /> : null}
          {tab === 'layers' ? <LayersTab {...props} /> : null}
          {tab === 'share' ? <ShareTab {...props} /> : null}
          {tab === 'audience' ? <AudienceTab {...props} /> : null}
          {tab === 'settings' ? <SettingsTab {...props} /> : null}
          {tab === 'history' ? <HistoryTab {...props} /> : null}
        </Suspense>
      </div>
    </div>
  );
}
